import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { Button } from "./ui/button";

export function SiteLogo({ variant = "light", size = 40 }) {
    const textColor = variant === "light" ? "text-white" : "text-[#0B2545]";
    return (
        <Link
            to="/"
            className={`flex items-center gap-3 font-serif ${textColor} transition-opacity hover:opacity-90`}
            data-testid="brand-logo"
        >
            <img
                src="/esss-logo.png?v=3"
                alt="ESSS"
                style={{ height: size, width: size }}
                className="rounded-md object-contain bg-white drop-shadow"
                data-testid="brand-logo-img"
            />
            <span className="text-xl font-semibold tracking-tight" data-testid="brand-wordmark">
                ESSS
            </span>
        </Link>
    );
}

const NAV_LINKS = [
    { to: "/about", label: "About", testId: "public-nav-about" },
    { to: "/contact", label: "Contact", testId: "public-nav-contact" },
];

export function PublicNav({ variant = "light" }) {
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const location = useLocation();
    const isLight = variant === "light";
    const linkColor = isLight ? "text-white/85 hover:text-white" : "text-[#0E1B2C]/75 hover:text-[#0B2545]";
    const barBg = isLight
        ? "bg-[#0B2545]/70 backdrop-blur-xl border-b border-white/10"
        : "bg-white/95 backdrop-blur-xl border-b border-[#D6E6F2]";

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 ${barBg}`}
            data-testid="site-header"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                <SiteLogo variant={variant} size={36} />

                <nav className="hidden md:flex items-center gap-8 text-sm">
                    {NAV_LINKS.map((n) => {
                        const active = location.pathname === n.to;
                        return (
                            <Link
                                key={n.to}
                                to={n.to}
                                data-testid={n.testId}
                                className={`transition-colors ${linkColor} ${active ? (isLight ? "text-white" : "text-[#0B2545]") : ""}`}
                            >
                                {n.label}
                                {active && (
                                    <motion.span
                                        layoutId="nav-underline"
                                        className={`block h-0.5 mt-1 ${isLight ? "bg-[#8AC4FF]" : "bg-[#1E56A0]"}`}
                                    />
                                )}
                            </Link>
                        );
                    })}
                </nav>

                <div className="flex items-center gap-2">
                    <Link to="/login" data-testid="header-signin-link" className="hidden sm:inline-block">
                        <Button
                            className={
                                isLight
                                    ? "rounded-full bg-white text-[#0B2545] hover:bg-[#D6E6F2] font-semibold h-10 px-5"
                                    : "rounded-full bg-[#0B2545] text-white hover:bg-[#1E56A0] font-semibold h-10 px-5"
                            }
                        >
                            Sign in
                            <ArrowRight className="ml-1" size={16} />
                        </Button>
                    </Link>
                    <button
                        onClick={() => setMobileOpen((v) => !v)}
                        className={`md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full ${isLight ? "text-white bg-white/10" : "text-[#0B2545] bg-[#D6E6F2]"}`}
                        aria-label="Toggle menu"
                        data-testid="public-nav-mobile-toggle"
                    >
                        {mobileOpen ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>
            {mobileOpen && (
                <div className={`md:hidden border-t ${isLight ? "border-white/10 bg-[#0B2545]/95" : "border-[#D6E6F2] bg-white"}`} data-testid="public-nav-mobile">
                    <div className="px-6 py-4 flex flex-col gap-3">
                        {NAV_LINKS.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                onClick={() => setMobileOpen(false)}
                                className={`text-sm ${linkColor}`}
                                data-testid={`${n.testId}-mobile`}
                            >
                                {n.label}
                            </Link>
                        ))}
                        <Link
                            to="/login"
                            onClick={() => setMobileOpen(false)}
                            className={`text-sm font-semibold ${isLight ? "text-[#8AC4FF]" : "text-[#1E56A0]"}`}
                            data-testid="header-signin-link-mobile"
                        >
                            Sign in →
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}

export function PublicFooter() {
    return (
        <footer
            className="bg-[#0B2545] border-t border-white/10 text-white/70 py-10 px-6 lg:px-10"
            data-testid="site-footer"
        >
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm">
                <div className="flex items-center gap-3">
                    <img src="/esss-logo.png?v=3" alt="ESSS" className="h-7 w-7 rounded object-contain bg-white" />
                    <span>© ESSS. Proprietary Framework Portfolio.</span>
                </div>
                <div className="flex items-center gap-6">
                    <Link to="/about" className="hover:text-white" data-testid="footer-about-link">About</Link>
                    <Link to="/contact" className="hover:text-white" data-testid="footer-contact-link">Contact</Link>
                    <Link to="/login" className="hover:text-white" data-testid="footer-signin-link">Sign In</Link>
                </div>
            </div>
        </footer>
    );
}
